/// <amd-module name="Widgets/PullRequestsWidget/components/PullRequestsWidget" />
import { WidgetProps } from "Dashboards/WidgetContracts/WidgetProps";
import * as React from "react";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { VisualTitle } from "Reporting/Visuals/VisualTitle";
import { NoDataMessage } from "Reporting/Visuals/VisualMessages/NoDataMessage";
import { UnconfiguredMessage } from "Reporting/Visuals/VisualMessages/UnconfiguredMessage";
import { PullRequestDataProvider, ICachedPullRequestData } from "Widgets/PullRequestsWidget/components/PullRequestDataProvider";
import { Dropdown } from "VSSUI/Dropdown";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";
import { GitPullRequest, GitRepository} from "Repos/Common/Contracts/Git";
import { IListBoxItem } from "VSSUI/ListBox";
import {
    Table,
    renderSimpleCell,
    TableColumnLayout,
    ITableColumn,
    TwoLineTableCell,
    SimpleTableCell
} from "VSSUI/Table";
import { ISimpleListCell } from "VSSUI/List";
import { ObservableValue } from "VSS/Core/Observable";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";
import { Tooltip } from "VSSUI/TooltipEx";
import { Link } from "VSSUI/Link";
import { Persona, PersonaSize } from "VSSUI/Persona";
import * as VS_Services from "VSS/Platform/Contracts/WebApi";
import {  PullRequestVoteStatus, PullRequestVoteIconMap, PullRequestVoteIconMessage } from 'Widgets/Configs/PullrequestWidgetConfig/PullRequestManager'
import * as Utils_Date from "VSSUI/Utilities/Date";
import * as Utils_String from "VSS/Core/Util/String";
import * as Resources from "Widgets/PullRequestsWidget/Resources";
import { IPageData, IVssPageService } from "VSS/Platform/Page";
import { ITfsPageService } from "Tfs/Platform/Page";

export interface IPullRequestsWidgetState {
    /**
     * Caches fetched pull request data
     * Clears out on widget refresh
    */
    pullRequestsResultsCache?: ICachedPullRequestData;
    
    selectedRepoId?: string
}

export interface ITableItem {
    creator: VS_Services.IdentityRef;
    pullRequest: IPullRequestTableCell;
    status: ISimpleListCell;
}

interface IPullRequestTableCell {
    title: string,
    repoName: string,
    targetBranch: string,
    createDate: Date,
    url: string
}

export class PullRequestsWidget extends VssComponent<WidgetProps, IPullRequestsWidgetState> {
    private selection: DropdownSelection;
    private dropdownItems: IListBoxItem<{}>[] = [];
    private hasMadeInitialSelection = false;
    public static componentType = "PullRequestsWidget";

    public constructor(props: WidgetProps, context: IVssComponentContext) {
        super(props, context);

        this.selection = new DropdownSelection();
        this.state = {
            pullRequestsResultsCache: {},
            selectedRepoId: undefined
        }
    }

    public async componentDidMount(): Promise<void> {
        if(this.props.settings){
            this.getPullRequestResults();
        }
        this.loadComponent(true);
    }

    public async componentDidUpdate(prevProps: WidgetProps, prevState: IPullRequestsWidgetState): Promise<void> {
        if(JSON.stringify(this.props) != JSON.stringify(prevProps) && this.props.settings){
            this.getPullRequestResults();
            this.loadComponent(true);
        }

        // select first repo from the dropdown when widget is loaded/ refreshed
        if(!this.hasMadeInitialSelection && this.dropdownItems.length){
            this.selection.select(0);
            this.hasMadeInitialSelection = true;
        }

        // trigger re-render and fetching of rows for pull request table if selected repo is changed
        if(this.selection.value.length && this.dropdownItems.length){
            if(this.dropdownItems[this.selection.value[0].beginIndex].id != this.state.selectedRepoId){
                this.setState({ selectedRepoId: this.dropdownItems[0].id})
            }
        }
    }

    private renderCreatorColumn = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<ITableItem>,
        tableItem: ITableItem
    ): JSX.Element => {
        const {creator} = tableItem;
        return (
            <SimpleTableCell columnIndex={columnIndex} key={columnIndex} tableColumn={tableColumn}>
                <Persona identity={creator} size={PersonaSize.size32} />
            </SimpleTableCell>
        );
    }

    private renderPullRequestVoteIcon = (pullRequest: GitPullRequest) => {
        const vote = PullRequestDataProvider.getMinimumVoteStatus(pullRequest);
        var className = "icon pull-request-widget-grid-icon-cell ";
        var tooltipText: string;

        if(PullRequestVoteStatus[vote] === "APPROVE"){
            className = className.concat(PullRequestVoteIconMap.APPROVE)
            tooltipText = PullRequestVoteIconMessage.APPROVE;
        }
        else if(PullRequestVoteStatus[vote] === "APPROVE_WITH_COMMENT"){
            className = className.concat(PullRequestVoteIconMap.APPROVE_WITH_COMMENT);
            tooltipText = PullRequestVoteIconMessage.APPROVE_WITH_COMMENT;
        }
        else if(PullRequestVoteStatus[vote] === "NOT_READY"){
            className = className.concat(PullRequestVoteIconMap.NOT_READY)
            tooltipText = PullRequestVoteIconMessage.NOT_READY;
        }
        else if(PullRequestVoteStatus[vote] === "REJECT"){
            className = className.concat(PullRequestVoteIconMap.REJECT)
            tooltipText = PullRequestVoteIconMessage.REJECT;
        }
        else{
            className = className.concat(PullRequestVoteIconMap.NONE);
            tooltipText = PullRequestVoteIconMessage.NONE;
        }

        return (
            <Tooltip text={tooltipText}>
                <div className={className} />
            </Tooltip>   
        )
    };

    private _roundUpToOneMinute(date: Date): Date {
        var diff = new Date().getTime() - date.getTime();
        if (diff < 60000) {
            return new Date(date.getTime() - (60000 - diff));
        }

        return date;
    }

    private renderPullRequestColumn = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<ITableItem>,
        tableItem: ITableItem
    ): JSX.Element => {
        const { creator, pullRequest } = tableItem;

        const createdTime = Utils_String.format(Resources.PullRequestWidgetCreatedDateFormat, Utils_Date.ago(this._roundUpToOneMinute(pullRequest.createDate)));
        const targetBranchSplit = pullRequest.targetBranch.split('/')
        const targetBranchName = targetBranchSplit[targetBranchSplit.length-1]

        const detailsSubHeading = Utils_String.format(
            Resources.PullRequestDetailsSubHeading, 
            creator.displayName,
            pullRequest.repoName,
            targetBranchName,
            createdTime
        )
        return (
            <TwoLineTableCell
                className="bolt-table-cell-content-with-inline-link no-v-padding"
                key={"col-" + columnIndex}
                columnIndex={columnIndex}
                tableColumn={tableColumn}
                line1={
                    <span className="flex-row scroll-hidden">
                        <Tooltip text={pullRequest.title} overflowOnly>
                            <Link
                                className="fontSizeM font-size-m text-ellipsis bolt-table-link bolt-table-inline-link"
                                excludeTabStop
                                href={pullRequest.url}
                            >
                                {pullRequest.title}
                            </Link>
                        </Tooltip>
                    </span>
                }
                line2={
                    <span className="fontSize font-size secondary-text flex-row flex-center text-ellipsis">
                        <Tooltip text={detailsSubHeading} overflowOnly>
                            <span className="bolt-table-link bolt-table-inline-link text-ellipsis" style={{flexShrink: 10}}>
                                {detailsSubHeading}
                            </span>
                        </Tooltip>
                    </span>
                }
            />
        );
    }

    private fixedColumns = [
        {
            id: "creator",
            readonly: true,
            renderCell: this.renderCreatorColumn,
            width: new ObservableValue(-10),
        },
        {
            columnLayout: TableColumnLayout.twoLine,
            id: "pullRequest",
            readonly: true,
            renderCell: this.renderPullRequestColumn,
            width: new ObservableValue(-80),
        },
        {
            id: "status",
            readonly: true,
            renderCell: renderSimpleCell,
            width: new ObservableValue(-10),
        },
    ];

    private getPullRequestTableItems = () => {

        const rawTableItems: ITableItem[] = [];
        if(this.state.selectedRepoId && this.state.pullRequestsResultsCache){
            this.state.pullRequestsResultsCache[this.state.selectedRepoId].PullRequests.forEach((pullRequest) => {
                rawTableItems.push({
                    creator: pullRequest.createdBy,
                    pullRequest: {
                        title: pullRequest.title,
                        createDate: pullRequest.creationDate,
                        repoName: pullRequest.repository.name,
                        targetBranch: pullRequest.targetRefName,
                        url: this.getPullRequestUrl(pullRequest.repository.name, pullRequest.pullRequestId)
                    },
                    status: { iconProps: { render: () => this.renderPullRequestVoteIcon(pullRequest)} },
                })
            })
        }
        return new ArrayItemProvider<ITableItem>(rawTableItems);
    }

    public render(): JSX.Element | null {
        if (!this.props.settings) {
            return <UnconfiguredMessage visualTitle={this.props.name} size={this.props.size} onClick={this.props.onConfigure} />;
        } else if (!this.state) {
            return null;
        } else {
            this.getDropdownItems();            
            if(this.dropdownItems.length){
                return this.renderPullRequestTable();
            }
            else return this.renderNoDataMessage();
        }
    }

    private renderPullRequestTable = () => {
        const tableItems = this.getPullRequestTableItems();
        var viewSummaryLink = "#";
        if(this.state.pullRequestsResultsCache && this.state.selectedRepoId){
            const repoName = this.state.pullRequestsResultsCache[this.state.selectedRepoId].RepositoryName;
            viewSummaryLink = this.getPullRequestUrl(repoName);
        }

        return (
            <div className="pull-request-widget">
                <div className="pull-request-widget-header">
                    <VisualTitle text={this.getTitleText()} />
                    <Dropdown
                        className="widget-repo-dropdown"
                        items={this.dropdownItems}
                        selection={this.selection}
                        onSelect={this.onRepoSelect}
                        placeholder="select"
                    />
                </div>
                <div className="flex-column relative v-scroll-auto custom-scrollbar custom-scrollbar-hidden" style={{"height":this.props.size.height-100}}>
                    <Table<Partial<ITableItem>>
                        ariaLabel="Pull Request Results"
                        columns={this.fixedColumns}
                        itemProvider={tableItems}
                        role="table"
                        containerClassName="h-scroll-auto"
                        className="PullRequestTable"
                    />
                </div> 
                <div className="pull-request-widget-footer">
                    <a href={viewSummaryLink} id="view-summary-link">View summary</a>
                </div>
            </div>
        )
    }
    private renderNoDataMessage = () => {
        return(
            <div className="pull-request-widget">
                <NoDataMessage visualTitle={this.getTitleText()} size={this.props.size} cardId={3} />
            </div>
        );
    }

    private getPullRequestResults = () => {
        if(this.props.settings){
            new PullRequestDataProvider(this.context.pageContext, this.props.settings, this.props.id!)
            .getResults(
                this.state.pullRequestsResultsCache, 
                this.updatePullRequestsResultsCache
            );
        }
    }

    private onRepoSelect = (event: React.SyntheticEvent<HTMLElement>, item: IListBoxItem<{}>) => {
        this.setState({selectedRepoId: item.id});
    }

    private getDropdownItems = (): void=> {
        const selectedRepos = this.props.settings ? JSON.parse(this.props.settings).selectedRepos : undefined;
        const items: IListBoxItem[] = [];

        if(selectedRepos){
            selectedRepos.forEach((repo: GitRepository) => {
                if(this.state.pullRequestsResultsCache && this.state.pullRequestsResultsCache[repo.id] && this.state.pullRequestsResultsCache[repo.id].Total > 0){
                    items.push({
                        id: repo.id,
                        text: repo.name
                    });
                }
            })
        }
        const sortedItems = items.sort((repo1, repo2) => {
            if(repo1.text && repo2.text){
                return repo1.text < repo2.text ? -1 : 1
            }
            return 0;
        });
        this.dropdownItems = sortedItems;
    }

    private updatePullRequestsResultsCache = (data: ICachedPullRequestData) => {
        this.setState({pullRequestsResultsCache: data});
    }

    private async loadComponent(isMounting: boolean, configCancelled?: boolean): Promise<void> {
        if (isMounting) {
            this.setState({});
            this.props.onWidgetLoaded();
        }
    }

    private getTitleText(): string {
        let title = this.props.name;
        return title;
    }

    private getPullRequestUrl(repositoryName: string, pullRequestId?: number) : string{
        return Utils_String.format(
            pullRequestId ? Resources.pull_request_discussion_page : Resources.pull_request_page,
            this.getHostUrl(),
            this.getServiceHostUrl(),
            this.getProjectName(),
            repositoryName,
            pullRequestId
        );
    }

    private getServiceHostUrl(): string | undefined {
        const pageService: IVssPageService = this.context.pageContext.getService<IVssPageService>("IVssPageService");
        const pageData: IPageData | undefined= pageService.getData();
        if(pageData) {
            return pageData.hostPath;
        }
    }

    private getProjectName(): string {
        const tfsPageService = this.context.pageContext.getService<ITfsPageService>("ITfsPageService");
        const tfsPageData = tfsPageService.getData();
        let projectName = "";
        if (!tfsPageData || !tfsPageData.project) {
            throw new Error("Project missing");
        } else {
            projectName = tfsPageData.project.name;
        }
        return projectName;
    }

    private getHostUrl(): string {
        return window.location.origin;
    }
    
}

VssComponent.register(PullRequestsWidget.componentType, PullRequestsWidget);
